import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, OnInit } from "@angular/core";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isInRange: boolean;
  isToday: boolean;
  isDisabled: boolean;
}
@Component({
  selector: "app-calender",
  templateUrl: "./calender.component.html",
  styleUrls: ["./calender.component.scss"],
  standalone: true,
  imports: [CommonModule],
})
export class CalenderComponent implements OnInit {
  @Input() initialStartDate: Date | null = null;
  @Input() initialEndDate: Date | null = null;
  @Input() disableFutureDates: boolean = true;

  @Output() rangeSelected = new EventEmitter<{
    startDate: Date;
    endDate: Date | null;
  }>();
  @Output() closeCalender = new EventEmitter<void>();

  months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // initialize as last month & this month
  private readonly today = new Date();
  currentViewDate = new Date(
    this.today.getFullYear(),
    this.today.getMonth() - 1,
    1,
  ); // last month
  nextViewDate = new Date(this.today.getFullYear(), this.today.getMonth(), 1); // this month

  startDate: Date | null = null;
  endDate: Date | null = null;
  activeQuickFilter: string = "custom";

  firstMonthDays: CalendarDay[] = [];
  secondMonthDays: CalendarDay[] = [];

  isYearSelectionMode = false;
  years: number[] = [];
  ComparisonNormalizer = ComparisonNormalizer;

  constructor() {
    this.generateYears();
  }

  ngOnInit(): void {
    if (this.initialStartDate) {
      this.startDate = new Date(this.initialStartDate);
      this.currentViewDate = new Date(
        this.startDate.getFullYear(),
        this.startDate.getMonth(),
        1,
      );
      this.nextViewDate = new Date(
        this.currentViewDate.getFullYear(),
        this.currentViewDate.getMonth() + 1,
        1,
      );
    }
    if (this.initialEndDate) {
      this.endDate = new Date(this.initialEndDate);
    }
    this.updateCalendarViews();
  }

  generateYears() {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 50; i <= currentYear + 50; i++) {
      this.years.push(i);
    }
  }

  toggleYearSelection() {
    this.isYearSelectionMode = !this.isYearSelectionMode;
    if (this.isYearSelectionMode) {
      // Scroll to current year (wait for render)
      setTimeout(() => {
        const activeYear = document.querySelector(".year-item.active");
        if (activeYear) {
          activeYear.scrollIntoView({ block: "center" });
        }
      }, 0);
    }
  }

  selectYear(year: number) {
    this.currentViewDate = new Date(year, this.currentViewDate.getMonth(), 1);
    this.nextViewDate = new Date(year, this.currentViewDate.getMonth() + 1, 1);
    this.isYearSelectionMode = false;
    this.updateCalendarViews();
  }

  navigateMonth(direction: number) {
    if (this.isYearSelectionMode) return;
    this.currentViewDate = new Date(
      this.currentViewDate.getFullYear(),
      this.currentViewDate.getMonth() + direction,
      1,
    );
    this.nextViewDate = new Date(
      this.currentViewDate.getFullYear(),
      this.currentViewDate.getMonth() + 1,
      1,
    );
    this.updateCalendarViews();
  }

  updateCalendarViews() {
    this.firstMonthDays = this.generateDays(this.currentViewDate);
    this.secondMonthDays = this.generateDays(this.nextViewDate);
  }

  generateDays(viewDate: Date): CalendarDay[] {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days: CalendarDay[] = [];

    const startPadding = firstDay.getDay();
    for (let i = startPadding - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push(this.createCalendarDay(d, false));
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push(this.createCalendarDay(d, true));
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      days.push(this.createCalendarDay(d, false));
    }

    return days;
  }

  createCalendarDay(date: Date, isCurrentMonth: boolean): CalendarDay {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    const isDisabled = this.disableFutureDates && d.getTime() > today.getTime(); // future dates disabled

    return {
      date: d,
      isCurrentMonth,
      isToday: d.getTime() === today.getTime(),
      isSelected: this.isDateSelected(d),
      isInRange: this.isDateInRange(d),
      isDisabled,
    };
  }

  isDateSelected(date: Date): boolean {
    return (
      this.startDate?.getTime() === date.getTime() ||
      this.endDate?.getTime() === date.getTime()
    );
  }

  isDateInRange(date: Date): boolean {
    if (!this.startDate || !this.endDate) return false;
    return (
      date.getTime() > this.startDate.getTime() &&
      date.getTime() < this.endDate.getTime()
    );
  }

  selectDate(date: Date) {
    // normalize input date to midnight
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // block future dates
    if (this.disableFutureDates && d.getTime() > today.getTime()) {
      return;
    }

    this.activeQuickFilter = "custom";
    if (!this.startDate || (this.startDate && this.endDate)) {
      this.startDate = date;
      this.endDate = null;
    } else if (date < this.startDate) {
      this.endDate = this.startDate;
      this.startDate = date;
    } else {
      this.endDate = date;
    }
    this.updateCalendarViews();
  }

  get isApplyDisabled(): boolean {
    const currentStart = this.normalizedTime(this.startDate);
    const currentEnd = this.normalizedTime(this.endDate);

    const initialStart = this.normalizedTime(this.initialStartDate);
    const initialEnd = this.normalizedTime(this.initialEndDate);

    const hasChanged =
      currentStart !== initialStart || currentEnd !== initialEnd;

    // Disable when nothing has changed
    return !hasChanged;
  }

  setQuickFilter(type: string) {
    this.activeQuickFilter = type;
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    switch (type) {
      case "today":
        this.startDate = new Date(now);
        this.endDate = new Date(now);
        break;
      case "yesterday": {
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        this.startDate = yesterday;
        this.endDate = yesterday;
        break;
      }
      case "last7": {
        const last7 = new Date(now);
        last7.setDate(now.getDate() - 6);
        this.startDate = last7;
        this.endDate = new Date(now);
        break;
      }
      case "last30": {
        const last30 = new Date(now);
        last30.setDate(now.getDate() - 29);
        this.startDate = last30;
        this.endDate = new Date(now);
        break;
      }
      case "thisMonth": {
        this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        this.endDate = this.disableFutureDates
          ? new Date(now)
          : new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      }
      case "lastMonth": {
        this.startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        this.endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      }
    }

    if (this.startDate) {
      this.currentViewDate = new Date(
        this.startDate.getFullYear(),
        this.startDate.getMonth(),
        1,
      );
      this.nextViewDate = new Date(
        this.currentViewDate.getFullYear(),
        this.currentViewDate.getMonth() + 1,
        1,
      );
    }
    this.updateCalendarViews();
  }

  getFormattedRange(): string {
    if (!this.startDate) return "Select range";
    const start = this.formatDate(this.startDate);
    if (!this.endDate) return start;
    return `${start} - ${this.formatDate(this.endDate)}`;
  }

  formatDate(date: Date): string {
    return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}/${date.getFullYear()}`;
  }

  onCancel() {
    this.closeCalender.emit();
  }

  onApply() {
    if (this.startDate) {
      this.endDate ??= this.startDate;
      this.rangeSelected.emit({
        startDate: this.startDate,
        endDate: this.endDate,
      });
    } else {
      // user cleared the range (e.g., via Reset) and then clicked Apply
      this.rangeSelected.emit({
        startDate: null as any,
        endDate: null,
      });
    }
    this.closeCalender.emit();
  }

  onReset() {
    this.startDate = null;
    this.endDate = null;
    this.activeQuickFilter = "custom";
    // reset view to last month & this month (same as initial)
    const now = new Date();
    this.currentViewDate = new Date(now.getFullYear(), now.getMonth() - 1, 1); // last month
    this.nextViewDate = new Date(now.getFullYear(), now.getMonth(), 1); // this month
    this.updateCalendarViews();
  }

  private normalizedTime(date: Date | null): number | null {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
}
